import React, { Fragment, useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
// import quizData from "./quizData.json";
import clsx from "clsx";
import screenfull from "screenfull";
import Countdown from "react-countdown";
import { useDispatch } from "react-redux";
import { Button, Container, Modal } from "react-bootstrap";
import { ConfirmModal, Question } from "@components";
import { ModalConfirm } from "@components/Modal";
import { QUIZ_STATUS, SECTION_ITEM_TYPE } from "@constants";
import { getPassingScore, getQuizResult, getTotalAnswered } from "@helpers";
import { markLessonCompletedAction } from "@redux/modules/course/slices";
import styles from "./styles/Quiz.module.css";

const MODAL_STATUS = {
  CLOSE: "CLOSE",
  CONFIRM: "CONFIRM",
  QUIZ_RESULT: "QUIZ_RESULT",
};

const Quiz = (props) => {
  const { data, hasNext, handleNext } = props;
  // state
  const [questions, setQuestions] = useState(data.questions);
  const [quizStatus, setQuizStatus] = useState(QUIZ_STATUS.INIT);
  const [quizResult, setQuizResult] = useState();
  const [currentModal, setCurrentModal] = useState(MODAL_STATUS.CLOSE);
  const [modalConfirm, setModalConfirm] = useState();
  const quizRef = useRef();
  const timeRef = useRef(Date.now());
  const countdownRef = useRef();
  const dispatch = useDispatch();

  // variables
  const showQuestion =
    quizStatus === QUIZ_STATUS.DOING || quizStatus === QUIZ_STATUS.REVIEW;
  const { maxScore, minScore } = getPassingScore(
    data.questions,
    data.passingScore
  );

  // logics before hooks

  // hooks

  // logics
  const showModalConfirm = (modalInfo) => {
    setModalConfirm(modalInfo);
    setCurrentModal(MODAL_STATUS.CONFIRM);
  };

  const showModalResult = () => {
    setCurrentModal(MODAL_STATUS.QUIZ_RESULT);
  };

  const closeModal = () => {
    setCurrentModal(MODAL_STATUS.CLOSE);
  };

  const startQuiz = () => {
    timeRef.current = Date.now();
    const newList = questions.map((q) => ({ ...q, userAnswer: [] }));
    setQuestions(newList);
    setQuizStatus(QUIZ_STATUS.DOING);
  };

  const handleChangeAnswer = (questionId, answer) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    if (questionIndex === -1) return;
    const newQuestion = { ...questions[questionIndex], userAnswer: answer };
    setQuestions([
      ...questions.slice(0, questionIndex),
      newQuestion,
      ...questions.slice(questionIndex + 1),
    ]);
  };

  const handleSubmitQuiz = (timeout = false) => {
    if (screenfull.isFullscreen) {
      toggleFullScreen();
    }
    if (quizStatus === QUIZ_STATUS.REVIEW) {
      setQuizStatus(QUIZ_STATUS.FINISH);
      return;
    }
    // Check number question answered
    if (
      getTotalAnswered(questions) < data.questionsLimit &&
      timeout === false
    ) {
      showModalConfirm({
        title: "Notification",
        body: `You need to answer at least ${data.questionsLimit} question(s) to submit`,
        submitButton: "Understood",
        showButtonCancel: false,
      });
      return;
    }
    // Confirm when time not out
    if (timeout === false) {
      showModalConfirm({
        title: "Confirm",
        body: data.isShowTime
          ? "You have time left. Do you want to submit now?"
          : "Do you want to submit now?",
        submitButton: "Submit",
        onClick: () => handleSubmitQuiz(true),
        onAfterSubmit: () => showModalResult(),
      });
      return;
    }

    const _quizResult = getQuizResult(questions, data.passingScore);
    setQuizResult(_quizResult);
    setQuizStatus(QUIZ_STATUS.FINISH);
    showModalResult();
  };

  const handleReviewQuiz = () => {
    closeModal();
    setQuizStatus(QUIZ_STATUS.REVIEW);
  };

  const handleRestartQuiz = () => {
    showModalConfirm({
      onClick: startQuiz,
      title: data.title,
      body: quizDescription,
      submitButton: "Restart",
    });
  };

  const toggleFullScreen = () => {
    screenfull.toggle(quizRef.current);
  };

  // UI partials
  const quizDescription = (
    <Fragment>
      <p className="m-0">
        <strong>Time: </strong>
        {data.isShowTime ? `${data.timeLimit} minutes` : "No limit"}
      </p>
      <p className="m-0">
        <strong>PassingScore: </strong>
        {data.passingScore}%
      </p>
      <p className="m-0">
        <strong>Description:</strong> {data.description}
      </p>
    </Fragment>
  );

  const resultInfo = ({ title, info, color }) => {
    return (
      <div className={styles.quizResultItem}>
        <div style={{ backgroundColor: color }}>{info}</div>
        <p>{title}</p>
      </div>
    );
  };

  const buttonNext = hasNext && (
    <p onClick={handleNext} className={styles.btnNext}>
      Go to next lesson
      {/* <i className="bi bi-chevron-right"></i> */}
    </p>
  );

  const resultSummary = quizResult ? (
    <div className="p-3">
      {quizResult.isPass ? (
        <div className={styles.quizPass}>
          <i className="bi bi-check-circle-fill"></i>
          <h4>Congratulations on passing the quiz</h4>
          {buttonNext}
        </div>
      ) : (
        <div className={styles.quizFail}>
          <i className="bi bi-x-circle-fill"></i>
          <h4>Sorry, you didn't pass the quiz</h4>
        </div>
      )}

      <div className={styles.quizResultItemWrapper}>
        {resultInfo({
          title: "Correct answer",
          info: `${quizResult.totalCorrectAnswer}/${quizResult.totalQuestions}`,
          color: "#4807EA",
        })}
        {resultInfo({
          title: "Your score",
          info: `${quizResult.totalScore}/${quizResult.maxScore}`,
          color: "#07AC18",
        })}
        {resultInfo({
          title: "Passing score",
          info: quizResult.minScore,
          color: "#FE7F0E",
        })}
      </div>
    </div>
  ) : data.isCompleted ? (
    <div className={styles.quizPass}>
      <i className="bi bi-check-circle-fill"></i>
      <h4>You have completed this quiz.</h4>
      {buttonNext}
    </div>
  ) : null;

  const buttonStart = (
    <ConfirmModal
      onSubmit={startQuiz}
      submitButton="Start"
      title={data.title}
      body={quizDescription}
      backdrop="static"
    >
      <Button className="mt-2">Start quiz</Button>
    </ConfirmModal>
  );

  const buttonRestart = (
    <Button onClick={() => handleRestartQuiz()}>Restart</Button>
  );

  const buttonReview = (outline = false) =>
    quizResult &&
    quizResult.isPass && (
      <Button
        onClick={handleReviewQuiz}
        className="ml-2"
        variant={outline ? "outline-primary" : "primary"}
      >
        Review
      </Button>
    );

  const buttonFullScreen = (
    <button onClick={toggleFullScreen} className={styles.btnFullScreen}>
      {screenfull.isFullscreen ? (
        <i className="bi bi-arrows-angle-contract"></i>
      ) : (
        <i className="bi bi-arrows-angle-expand"></i>
      )}
    </button>
  );

  const renderTimer = ({ hours, formatted, total }) => {
    return (
      <span>
        <i
          className={clsx("bi bi-alarm mr-2", total <= 10000 && styles.shaking)}
        ></i>
        {hours !== 0 && `${formatted.hours}:`}
        {formatted.minutes}:{formatted.seconds}
      </span>
    );
  };

  const quizTimer = data?.isShowTime && (
    <Countdown
      ref={countdownRef}
      date={timeRef.current + data.timeLimit * 60000}
      renderer={renderTimer}
      onComplete={() => handleSubmitQuiz(true)}
    />
  );

  // effects
  useEffect(() => {
    setQuestions(data.questions);
    if (data.isCompleted) {
      setQuizStatus(QUIZ_STATUS.FINISH);
    } else {
      setQuizStatus(QUIZ_STATUS.INIT);
    }
    return () => {
      setQuestions([]);
      setQuizResult(null);
    };
  }, [data.id]);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
  }, [quizStatus]);

  useEffect(() => {
    if (quizResult?.isPass && data.id && !data.isCompleted) {
      dispatch(
        markLessonCompletedAction({
          id: data.id,
          type: SECTION_ITEM_TYPE.QUIZ,
        })
      );
    }
  }, [quizResult]);

  // render
  return (
    <div className={styles.container} ref={quizRef}>
      <Container className={clsx("px-5 pt-4", styles.mainContent)} fluid>
        <h2>
          <i className="bi bi-lightbulb mr-3"></i>
          {data.title}
        </h2>

        {/* Start quiz */}
        {quizStatus === QUIZ_STATUS.INIT && (
          <Fragment>
            {quizDescription}
            {buttonStart}
          </Fragment>
        )}

        {/* Quiz result summary */}
        {quizStatus === QUIZ_STATUS.FINISH && (
          <Fragment>
            {resultSummary}
            <div className="d-flex gap-2 justify-content-center">
              {buttonRestart}
              {buttonReview(true)}
            </div>
          </Fragment>
        )}

        {/* List question */}
        {showQuestion && (
          <div className={clsx(styles.quizContainer)}>
            {questions.map((question, index) => (
              <Question
                key={question.id}
                {...question}
                title={`${index + 1}. ${question.title}`}
                onChangeAnswer={(answer) =>
                  handleChangeAnswer(question.id, answer)
                }
                showAnswer={quizStatus === QUIZ_STATUS.REVIEW}
              />
            ))}
          </div>
        )}
      </Container>

      {/* Quiz control */}
      {showQuestion && (
        <div className={clsx("px-5", styles.quizControlWrapper)}>
          <div>
            <Button className="mr-3" onClick={() => handleSubmitQuiz()}>
              {quizStatus === QUIZ_STATUS.DOING ? "Submit" : "Cancel"}
            </Button>
            {quizStatus === QUIZ_STATUS.DOING && quizTimer}
          </div>
          {buttonFullScreen}
        </div>
      )}

      {/* Modals */}
      <ModalConfirm
        show={currentModal === MODAL_STATUS.CONFIRM}
        handleClose={() => closeModal()}
        onClick={() => {
          modalConfirm?.onClick && modalConfirm.onClick();
          closeModal();
          modalConfirm?.onAfterSubmit && modalConfirm.onAfterSubmit();
        }}
        title={modalConfirm ? modalConfirm.title : "Notification"}
        body={modalConfirm ? modalConfirm.body : null}
        submitButton={modalConfirm ? modalConfirm.submitButton : "Ok"}
        showButtonCancel={modalConfirm ? modalConfirm.showButtonCancel : true}
      />

      <Modal
        show={currentModal === MODAL_STATUS.QUIZ_RESULT}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Body>{resultSummary}</Modal.Body>
        <Modal.Footer>
          <Button onClick={() => closeModal()} variant="outline-primary">
            Cancel
          </Button>
          {quizResult && (quizResult.isPass ? buttonReview() : buttonRestart)}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

/* required: display name */
Quiz.displayName = "Quiz";

/* required: props types */
Quiz.propTypes = { questionList: PropTypes.array };

/* required: default props */
Quiz.defaultProps = {};

export { Quiz };
